import { Controller } from '@hotwired/stimulus';
import $ from 'jquery';

function buildDataTablesLanguage(i18n) {
    return {
        entries: {
            1: i18n.entryOne,
            _: i18n.entryOther,
        },
        lengthLabels: {
            '-1': i18n.all,
        },
        sLengthMenu: i18n.lengthMenu,
        sSearch: i18n.search,
        sZeroRecords: i18n.zeroRecords,
        fnInfoCallback(settings, start, end, max, total, _out) {
            const fmt = settings.fnFormatNumber.bind(settings);
            if (total === 0) {
                return i18n.infoEmpty;
            }
            const word = total === 1 ? i18n.entryOne : i18n.entryAfterTotal;
            let main = i18n.infoMain
                .replace(/\[start\]/g, fmt(start))
                .replace(/\[end\]/g, fmt(end))
                .replace(/\[total\]/g, fmt(total))
                .replace(/\[word\]/g, word);
            if (total !== max) {
                const fw = max === 1 ? i18n.entryOne : i18n.entryAfterTotal;
                const filtered = i18n.infoFiltered
                    .replace(/\[max\]/g, fmt(max))
                    .replace(/\[word\]/g, fw);
                return `${main} ${filtered}`;
            }
            return main;
        },
    };
}

export default class extends Controller {
    static values = {
        datatable: Boolean,
        i18n: { type: Object, default: {} },
    };

    connect() {
        const tables = this.element.querySelectorAll('table');

        tables.forEach((table) => {
            if (table.parentElement && table.parentElement.classList.contains('table-container')) {
                return;
            }

            if (table.closest && table.closest('.dt-container')) {
                return;
            }

            if (this.datatableValue && $.fn.DataTable && $.fn.DataTable.isDataTable(table)) {
                return;
            }

            const wrapper = document.createElement('div');
            wrapper.classList.add('table-container');

            table.parentNode.insertBefore(wrapper, table);
            wrapper.appendChild(table);

            if (this.datatableValue && $.fn.DataTable) {
                this.ensureHeaderRow(table);

                const expectedColumns = this.getExpectedColumnCount(table);

                if (expectedColumns <= 0) {
                    return;
                }

                if (!$.fn.DataTable.isDataTable(table)) {
                    if (!table.id) {
                        table.id = `datatable-${this.element.id || 't'}-${Math.random().toString(36).slice(2, 10)}`;
                    }
                    const options = {
                        pageLength: 10,
                        lengthMenu: [[10, 25, 50, 100, -1], [10, 25, 50, 100, 'All']],
                    };
                    const i18n = this.i18nValue;
                    if (i18n && typeof i18n === 'object' && Object.keys(i18n).length > 0) {
                        options.oLanguage = buildDataTablesLanguage(i18n);
                        options.lengthMenu = [
                            [10, 25, 50, 100, -1],
                            [10, 25, 50, 100, i18n.all],
                        ];
                    }
                    $(table).DataTable(options);
                    this.wrapTableScroll(wrapper);
                }
            }
        });
    }

    wrapTableScroll(tableContainer) {
        const dtContainer = tableContainer.querySelector('.dt-container');
        if (!dtContainer || dtContainer.querySelector('.dt-table-scroll-wrapper')) return;
        const children = Array.from(dtContainer.children);
        if (children.length < 2) return;

        const scrollWrapper = document.createElement('div');
        scrollWrapper.classList.add('dt-table-scroll-wrapper');
        scrollWrapper.appendChild(children[1]);
        children[0].after(scrollWrapper);
    }

    ensureHeaderRow(table) {
        if (table.querySelector('thead')) {
            return;
        }

        const firstBodyRow = table.querySelector('tbody tr');
        const firstRow = firstBodyRow || table.querySelector('tr');

        if (!firstRow) {
            return;
        }

        const thead = document.createElement('thead');
        const headerRow = firstRow.cloneNode(true);

        const safeAttrs = ['colspan', 'rowspan', 'class'];
        headerRow.querySelectorAll('td, th').forEach((cell) => {
            if (cell.tagName.toLowerCase() !== 'th') {
                const th = document.createElement('th');
                th.textContent = cell.textContent;
                safeAttrs.forEach((name) => {
                    const val = cell.getAttribute(name);
                    if (val != null) th.setAttribute(name, val);
                });
                th.setAttribute('scope', 'col');
                cell.replaceWith(th);
            } else {
                cell.setAttribute('scope', 'col');
            }
        });

        thead.appendChild(headerRow);
        table.insertBefore(thead, table.firstChild);

        if (firstRow.parentElement && firstRow.parentElement.tagName.toLowerCase() === 'tbody') {
            firstRow.remove();
        }
    }

    getExpectedColumnCount(table) {
        const headerRow = table.querySelector('thead tr');
        const firstRow = headerRow || table.querySelector('tr');

        if (!firstRow) {
            return 0;
        }

        return this.getRowColumnCount(firstRow);
    }

    getRowColumnCount(row) {
        const cells = row.querySelectorAll('th, td');

        if (!cells.length) {
            return 0;
        }

        let count = 0;

        cells.forEach((cell) => {
            const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);
            count += Number.isNaN(colspan) ? 1 : colspan;
        });

        return count;
    }
}
